import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {ActiveJobsTable} from './ActiveJobsTable'
import {ModernDashboardWidget} from './ModernDashboardWidget'
import {ModernRevenueChart} from './ModernRevenueChart'
import {ModernStatusChart} from './ModernStatusChart'
import {WaterWashCard} from './WaterWashCard'
import {JobsActivityCard} from './JobsActivityCard'
import {ActivityHeatMapCard} from './ActivityHeatMapCard'
import {DashboardStatsTable} from './DashboardStatsTable'
import {getFinanceDashboard, getFinanceTableMetrics} from '../generic/crud'
import {CacheTimerBadge} from './CacheTimerBadge'
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'

export const FinanceDashboardPage = () => {
  const {mode} = useThemeMode()
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark')
  const [activeJobs, setActiveJobs] = useState<any[]>([])
  const [closedTodayJobs, setClosedTodayJobs] = useState<any[]>([])
  const [allClosedJobs, setAllClosedJobs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshCounter, setRefreshCounter] = useState(0)
  const [tableMetrics, setTableMetrics] = useState<any>(null)
  const [tableMetricsLoading, setTableMetricsLoading] = useState(true)
  const [countdown, setCountdown] = useState<number>(0)
  const [cacheExpiry, setCacheExpiry] = useState<string | null>(null)
  const [waterWash, setWaterWash] = useState<{today: number, thisWeek: number, thisMonth: number}>({today: 0, thisWeek: 0, thisMonth: 0})

  useEffect(() => {
    fetchDashboardData()
    fetchTableMetrics()
  }, [refreshCounter])

  // Countdown timer for cache expiry
  useEffect(() => {
    if (!cacheExpiry) return

    const updateCountdown = () => {
      const now = new Date().getTime()
      const expiry = new Date(cacheExpiry).getTime()
      const remaining = Math.max(0, Math.floor((expiry - now) / 1000))
      setCountdown(remaining)

      if (remaining <= 0) {
        clearInterval(interval)
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [cacheExpiry])

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      // Single API call to fetch all finance dashboard data
      const response = await getFinanceDashboard()
      const data = response.data
      setCacheExpiry(data.cacheExpiry || null)
      setWaterWash({today: data.waterWashToday || 0, thisWeek: data.waterWashThisWeek || 0, thisMonth: data.waterWashThisMonth || 0})

      // Combine open and in-progress jobs for active jobs
      const openJobs = data.openJobs || []
      const inProgressJobs = data.inProgressJobs || []
      const allActiveJobs = [...openJobs, ...inProgressJobs]

      setActiveJobs(allActiveJobs)
      setClosedTodayJobs(data.closedToday || [])
      setAllClosedJobs(data.closedThisMonth || [])

      console.log('✅ Finance Dashboard data loaded in single API call')
      console.log('📊 Active Jobs:', allActiveJobs.length)
      console.log('📊 Closed Today:', data.closedToday?.length || 0)
    } catch (error) {
      console.error('❌ Error fetching finance dashboard data:', error)
      setActiveJobs([])
      setClosedTodayJobs([])
      setAllClosedJobs([])
    } finally {
      setLoading(false)
    }
  }

  const fetchTableMetrics = async () => {
    setTableMetricsLoading(true)
    try {
      const response = await getFinanceTableMetrics()
      setTableMetrics(response.data)
      console.log('✅ Table metrics loaded')
    } catch (error) {
      console.error('❌ Error fetching table metrics:', error)
      setTableMetrics(null)
    } finally {
      setTableMetricsLoading(false)
    }
  }

  // Revenue Calculations
  const calculateJobValue = (job: any) => {
      if (!job.selectedTreatments) return 0;
      return job.selectedTreatments.reduce((sum: number, t: any) => sum + (t.customerPrice || 0), 0);
  }

  const revenueToday = closedTodayJobs.reduce((sum, job) => sum + calculateJobValue(job), 0);
  const revenuePipeline = activeJobs.reduce((sum, job) => sum + calculateJobValue(job), 0);
  const totalActiveJobs = activeJobs.length;
  const todayClosedCount = closedTodayJobs.length;

  // Average job value
  const avgJobValue = totalActiveJobs > 0 ? revenuePipeline / totalActiveJobs : 0;

  const handleRefresh = () => {
    setRefreshCounter(prev => prev + 1)
  }

  // Dummy trend data
  const revenueSeries = [{ name: 'Revenue', data: [revenueToday * 0.7, revenueToday * 0.9, revenueToday] }]
  const pipelineSeries = [{ name: 'Pipeline', data: [revenuePipeline * 0.95, revenuePipeline] }]
  const closedSeries = [{ name: 'Closed', data: [todayClosedCount - 2 > 0 ? todayClosedCount - 2 : 0, todayClosedCount] }]
  const avgSeries = [{ name: 'Avg Value', data: [avgJobValue, avgJobValue * 1.05] }]

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-6">
        <div>
          <h1 className="text-gray-900 fw-bolder fs-1 mb-2">Finance Dashboard</h1>
          <span className="text-muted fw-bold fs-6">
            Financial metrics and revenue tracking
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {cacheExpiry && countdown > 0 && (
            <CacheTimerBadge countdown={countdown} formatCountdown={formatCountdown} />
          )}
          <button
            className="btn btn-sm btn-light-primary"
            onClick={handleRefresh}
            disabled={loading}
          >
            <i className={`bi bi-arrow-clockwise me-2 ${loading ? 'spinner-border spinner-border-sm' : ''}`}></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Row 1: KPI Cards */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-6 col-md-6 col-xl-3">
          <ModernDashboardWidget
            title="Today's Revenue"
            description="Collected today"
            value={`₹${revenueToday.toLocaleString()}`}
            icon="bi-cash-stack"
            color="#50cd89" // Green
            chartSeries={revenueSeries}
            chartColor="#50cd89"
          />
        </div>
        <div className="col-6 col-md-6 col-xl-3">
          <ModernDashboardWidget
            title="Pipeline Value"
            description="Active jobs value"
            value={`₹${revenuePipeline.toLocaleString()}`}
            icon="bi-graph-up-arrow"
            color="#009ef7" // Blue
            chartSeries={pipelineSeries}
            chartColor="#009ef7"
          />
        </div>
        <div className="col-6 col-md-6 col-xl-3">
          <ModernDashboardWidget
            title="Jobs Completed"
            description="Finished today"
            value={todayClosedCount}
            icon="bi-check-circle-fill"
            color="#7239ea" // Purple
            chartSeries={closedSeries}
            chartColor="#7239ea"
          />
        </div>
        <div className="col-6 col-md-6 col-xl-3">
           <ModernDashboardWidget
            title="Avg Job Value"
            description="Per active job"
            value={`₹${Math.round(avgJobValue).toLocaleString()}`}
            icon="bi-calculator"
            color="#ffc700" // Yellow
            chartSeries={avgSeries}
            chartColor="#ffc700"
          />
        </div>
      </div>

      {/* Row 2: Quick Actions */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-6">
          <Link to="/jobCards" className="card h-100 bg-primary hover-elevate-up" style={{textDecoration: 'none'}}>
            <div className="card-body d-flex flex-column justify-content-center align-items-center text-center p-5 p-md-9">
                <i className="bi bi-file-earmark-spreadsheet fs-2x fs-md-3x text-white mb-3 mb-md-5"></i>
                <div className="fs-4 fs-md-2 fw-bolder text-white mb-1 mb-md-2">View Reports</div>
                <div className="fw-bold text-white opacity-50 d-none d-md-block">Financial analysis</div>
            </div>
          </Link>
        </div>
        <div className="col-6">
          <Link to="/dashboard" className="card h-100 bg-body shadow-sm hover-elevate-up" style={{textDecoration: 'none'}}>
            <div className="card-body d-flex flex-column justify-content-center align-items-center text-center p-5 p-md-9">
                <i className="bi bi-graph-up fs-2x fs-md-3x text-success mb-3 mb-md-5"></i>
                <div className="fs-4 fs-md-2 fw-bolder ${isDarkMode ? 'text-white' : 'text-dark'} mb-1 mb-md-2">Analytics</div>
                <div className="fw-bold text-gray-400 d-none d-md-block">Performance data</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Row 3: Water Wash, Jobs Activity & Heatmap */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-4">
          <WaterWashCard className="h-100" today={waterWash.today} thisWeek={waterWash.thisWeek} thisMonth={waterWash.thisMonth} />
        </div>
        <div className="col-xl-4">
          <JobsActivityCard
            className="h-100"
            activeJobs={activeJobs}
            closedJobs={closedTodayJobs}
            loading={loading}
          />
        </div>
        <div className="col-xl-4">
            <ActivityHeatMapCard
                className="h-100"
                allClosedJobs={allClosedJobs}
                allActiveJobs={activeJobs}
                loading={loading}
            />
        </div>
      </div>

      {/* Row 4: Charts */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-12">
           <ModernRevenueChart jobs={activeJobs} loading={loading} />
        </div>
      </div>

      {/* Row 5: Dashboard Stats Table & Status Chart */}
      <div className="row g-5 g-xl-10 mb-5 mb-xl-10">
        <div className="col-xl-8">
            <DashboardStatsTable
              metrics={tableMetrics}
              loading={tableMetricsLoading}
            />
        </div>
        <div className="col-xl-4">
           <ModernStatusChart jobs={activeJobs} loading={loading} />
        </div>
      </div>

      {/* Summary Info Card */}
      <div className="card mb-5 mb-xl-8">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold fs-3 mb-1">Financial Summary</span>
            <span className="text-muted mt-1 fw-semibold fs-7">Revenue breakdown and insights</span>
          </h3>
        </div>
        <div className="card-body py-3">
          <div className="table-responsive">
            <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
              <thead>
                <tr className="fw-bold text-muted">
                  <th className="min-w-150px">Metric</th>
                  <th className="min-w-120px">Value</th>
                  <th className="min-w-120px">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="fw-bold">Total Active Jobs</td>
                  <td className="${isDarkMode ? 'text-white' : 'text-dark'} fw-bold">{totalActiveJobs}</td>
                  <td className="text-muted">Jobs currently in progress</td>
                </tr>
                <tr>
                  <td className="fw-bold">Pipeline Revenue</td>
                  <td className="${isDarkMode ? 'text-white' : 'text-dark'} fw-bold">₹{revenuePipeline.toLocaleString()}</td>
                  <td className="text-muted">Expected revenue from active jobs</td>
                </tr>
                <tr>
                  <td className="fw-bold">Today's Revenue</td>
                  <td className="text-success fw-bold">₹{revenueToday.toLocaleString()}</td>
                  <td className="text-muted">Revenue from jobs closed today</td>
                </tr>
                <tr>
                  <td className="fw-bold">Average Job Value</td>
                  <td className="text-primary fw-bold">₹{Math.round(avgJobValue).toLocaleString()}</td>
                  <td className="text-muted">Mean value per active job</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}